import React, { useState } from "react";
import { Phone, MessageCircle, Mail, Clock, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import useSEO from "../hooks/useSEO";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Contact = () => {
  const { lang, tr } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  useSEO({ title: `${tr.contactPage.title} — MobiRider`, description: tr.contactPage.subtitle, lang });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(tr.contactPage.sent);
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Error"); } finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 md:py-20">
      <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-ink tracking-tight">{tr.contactPage.title}</h1>
      <p className="text-xl text-ink-muted mt-4">{tr.contactPage.subtitle}</p>
      <div className="grid gap-10 md:grid-cols-2 mt-12">
        <div className="space-y-5">
          <a href="tel:+33788041772" className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:border-brand">
            <div className="w-14 h-14 rounded-2xl bg-brand/10 grid place-items-center"><Phone className="w-7 h-7 text-brand" /></div>
            <div><div className="text-sm text-ink-muted">{tr.contactPage.phone}</div><div className="text-lg font-bold text-ink">07 88 04 17 72</div></div>
          </a>
          <a href="https://wa.me/33788041772" className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:border-brand">
            <div className="w-14 h-14 rounded-2xl bg-green-100 grid place-items-center"><MessageCircle className="w-7 h-7 text-green-600" /></div>
            <div><div className="text-sm text-ink-muted">WhatsApp</div><div className="text-lg font-bold text-ink">07 88 04 17 72</div></div>
          </a>
          <a href="mailto:thibault.milhaud@gmail.com" className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:border-brand">
            <div className="w-14 h-14 rounded-2xl bg-accent2/10 grid place-items-center"><Mail className="w-7 h-7 text-accent2" /></div>
            <div><div className="text-sm text-ink-muted">Email</div><div className="text-lg font-bold text-ink">thibault.milhaud@gmail.com</div></div>
          </a>
          <div className="flex items-center gap-4 bg-cream rounded-2xl p-6">
            <Clock className="w-7 h-7 text-brand" /><span className="text-lg text-ink">{tr.contactPage.hours}</span>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-3xl p-7 border border-slate-100 shadow-md space-y-4" data-testid="contact-form">
          <div>
            <label className="block text-lg font-semibold text-ink mb-2">{tr.contactPage.name}</label>
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
          </div>
          <div>
            <label className="block text-lg font-semibold text-ink mb-2">Email</label>
            <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
          </div>
          <div>
            <label className="block text-lg font-semibold text-ink mb-2">{tr.contactPage.message}</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border-2 border-slate-200 focus:border-brand px-4 py-3 text-lg" />
          </div>
          <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full h-14 rounded-full bg-accent2 hover:bg-accent2-hover text-white font-bold text-lg">
            <Send className="w-5 h-5 mr-2" /> {tr.contactPage.send}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
